import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CalendarClock, CheckCircle2, MapPin, Clock, X } from 'lucide-react';
import { publicPrestationReservationApi } from '../../lib/api';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';

interface PassageRestant {
    numero: number | null;
    periode: string;
    lignes: { label: string; quantity: number; catalog_item_id?: string | null; unit_duration_minutes?: number }[];
    // Visites multiples : le client peut répartir ce passage sur plusieurs jours (une part à chaque visite).
    visites_multiples?: boolean;
    avancement?: { total_prevu: number; total_fait: number; pourcentage: number; nb_visites: number } | null;
    duration_minutes: number;
    duree_estimee: boolean;
}
interface RdvAVenir { date: string; start_time: string; status: string; passage: number | null }
interface CatalogueExtra { id: string; label: string; unit_type: string; duration_minutes?: number }
interface ReservationInfo {
    company_name: string;
    logo_url: string | null;
    brand_color: string;
    label: string;
    prenom: string;
    etat: 'en_attente' | 'reserve' | 'clos';
    sans_formule: boolean;
    // Tous les passages encore dus (2026-09-26) — avant, seul le premier passage était jamais proposé.
    passages_restants: PassageRestant[];
    rendez_vous_a_venir: RdvAVenir[];
    extras_allowed: boolean;
    catalogue_extras: CatalogueExtra[];
    hors_contrat_catalogue?: CatalogueExtra[];
    location_mode: 'domicile' | 'entreprise' | 'les_deux';
    adresse: { address: string; postal_code: string; city: string };
    // Adresse fixe (réglage de l'établissement) : affichée, jamais ressaisie.
    adresse_fixe?: boolean;
    // Contrat signé à domicile : premier jour d'intervention sans condition (null = pas de limite), premier jour
    // possible si le client demande l'intervention anticipée, fin de la rétractation et texte exact de la demande.
    date_min?: string | null;
    date_min_anticipee?: string | null;
    fin_retractation?: string | null;
    texte_anticipation?: string | null;
}

const fmtDateLongue = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
};
const fmtDuree = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, '0')}` : ''}` : `${m} min`);
const aujourdhui = () => new Date().toISOString().split('T')[0];

/**
 * Page publique de réservation du rendez-vous après devis signé (lot B, 2026-09-22) — même gabarit que
 * PrestationDevisSignaturePage.tsx (logo, carte, lien personnel). Le client choisit une date puis un
 * créneau libre ; le rendez-vous est créé « en demande » côté établissement, qui assigne l'équipe et
 * confirme depuis le Planning — exactement comme une Démarche client.
 */
export const PrestationReservationPage: React.FC = () => {
    // Page publique sur fond blanc, comme le simulateur (2026-10-15, demande d'Emeni) : elle suit le même
    // parcours, le client ne doit pas passer du clair au sombre selon le thème de son appareil. Les jetons de
    // couleur vivent sur <html> : on force le thème clair le temps de la page, puis on le rend tel quel.
    React.useEffect(() => {
        const root = document.documentElement;
        const themeAvant = root.getAttribute('data-theme');
        const eraitSombre = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        return () => {
            if (themeAvant === null) root.removeAttribute('data-theme'); else root.setAttribute('data-theme', themeAvant);
            if (eraitSombre) root.classList.add('dark');
        };
    }, []);

    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<ReservationInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    // Passage choisi parmi ceux encore dus (2026-09-26) — undefined tant que rien n'est choisi, null pour un
    // devis classique (un seul "passage" implicite, pas de numéro).
    const [passageChoisi, setPassageChoisi] = React.useState<number | null | undefined>(undefined);
    const [date, setDate] = React.useState('');
    const [anticipationCochee, setAnticipationCochee] = React.useState(false);
    const [slots, setSlots] = React.useState<string[]>([]);
    const [slotsLoading, setSlotsLoading] = React.useState(false);
    const [heure, setHeure] = React.useState('');
    const [lieu, setLieu] = React.useState<'domicile' | 'entreprise'>('domicile');
    const [adresse, setAdresse] = React.useState('');
    const [codePostal, setCodePostal] = React.useState('');
    const [ville, setVille] = React.useState('');
    const [extras, setExtras] = React.useState<{ catalog_item_id: string; quantity: number }[]>([]);
    const [extraAjoutId, setExtraAjoutId] = React.useState('');
    const [envoi, setEnvoi] = React.useState(false);
    // Passage supplémentaire hors contrat, ouvert depuis l'espace client (?mode=hors-contrat).
    const [horsContrat, setHorsContrat] = React.useState(() => new URLSearchParams(window.location.search).get('mode') === 'hors-contrat');
    const [confirme, setConfirme] = React.useState<{ date: string; start_time: string } | null>(null);
    // Visite partielle (2026-10-19, comme Jobber / ServiceTitan) : la part du travail faite à CETTE visite.
    const [partVisite, setPartVisite] = React.useState<Record<string, number>>({});
    const [resteApres, setResteApres] = React.useState<number | null>(null);
    // Durée du passage hors contrat : somme des durées des prestations choisies (60 min si aucune n'a de durée).
    const dureeHorsContrat = Math.max(30, Math.round(extras.reduce((n, e) => n + e.quantity * (Number((info?.hors_contrat_catalogue || []).find((c) => c.id === e.catalog_item_id)?.duration_minutes) || 0), 0)) || 60);

    // Retour du navigateur après un paiement obligatoire (redirection Stripe/Revolut vers cette même page,
    // cf. GET /api/reservation-paiement/:id/finalize) — la page a rechargé entièrement, tout l'état de
    // sélection ci-dessus est donc perdu : un message générique suffit.
    const [paymentReturn] = React.useState<'ok' | 'error' | null>(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment') === 'ok') return 'ok';
        if (params.get('error') === 'payment_incomplete') return 'error';
        return null;
    });

    React.useEffect(() => {
        if (!token) return;
        publicPrestationReservationApi.getInfo(token)
            .then((d: any) => {
                setInfo(d);
                setLieu(d?.location_mode === 'entreprise' ? 'entreprise' : 'domicile');
                setAdresse(d?.adresse?.address || '');
                setCodePostal(d?.adresse?.postal_code || '');
                setVille(d?.adresse?.city || '');
                // Un seul passage restant (le cas courant) : autant le présélectionner plutôt que de forcer
                // un clic supplémentaire sur une seule option.
                const restants: PassageRestant[] = Array.isArray(d?.passages_restants) ? d.passages_restants : [];
                if (restants.length === 1) setPassageChoisi(restants[0].numero);
                if (new URLSearchParams(window.location.search).get('mode') === 'hors-contrat') setPassageChoisi(null);
            })
            .catch((e: any) => setErreur(e?.message || 'Lien invalide ou expiré.'))
            .finally(() => setLoading(false));
    }, [token]);

    // Passage choisi et part de la visite, calculés AVANT les effets qui en dépendent.
    const passageActuel = horsContrat ? null : (info?.passages_restants.find((p) => p.numero === passageChoisi) || null);
    const cleLigne = (l: { label: string; catalog_item_id?: string | null }) => String(l.catalog_item_id ?? l.label);
    const visiteLignes = passageActuel && passageActuel.visites_multiples
        ? passageActuel.lignes.map((l) => ({ ...l, cle: cleLigne(l), q: Math.max(0, Math.min(l.quantity, partVisite[cleLigne(l)] ?? l.quantity)) }))
        : [];
    const dureeVisite = visiteLignes.length > 0
        ? Math.max(30, Math.round(visiteLignes.reduce((n, l) => n + l.q * (Number(l.unit_duration_minutes) || 0), 0)) || 60)
        : 0;
    React.useEffect(() => {
        if (!token || !date || passageChoisi === undefined) { setSlots([]); return; }
        let annule = false;
        setSlotsLoading(true); setHeure(''); setErreur('');
        publicPrestationReservationApi.getAvailability(token, date, horsContrat ? 'hors' : passageChoisi, horsContrat ? dureeHorsContrat : (dureeVisite > 0 ? dureeVisite : undefined))
            .then((r: any) => { if (!annule) setSlots(Array.isArray(r?.slots) ? r.slots : []); })
            .catch(() => { if (!annule) setSlots([]); })
            .finally(() => { if (!annule) setSlotsLoading(false); });
        return () => { annule = true; };
    }, [token, date, passageChoisi, horsContrat, dureeHorsContrat, dureeVisite]);

    const ajouterExtra = (id: string) => {
        if (!id || extras.some((e) => e.catalog_item_id === id)) return;
        setExtras((prev) => [...prev, { catalog_item_id: id, quantity: 1 }]);
        setExtraAjoutId('');
    };
    const changerQuantiteExtra = (id: string, q: number) =>
        setExtras((prev) => prev.map((e) => (e.catalog_item_id === id ? { ...e, quantity: Math.max(1, Math.min(99, Math.round(q) || 1)) } : e)));
    const retirerExtra = (id: string) => setExtras((prev) => prev.filter((e) => e.catalog_item_id !== id));

    // Contrat signé à domicile : une date avant la fin du délai de rétractation exige la demande cochée par le client.
    const dateAnticipee = !!(info?.date_min && info.date_min_anticipee && date && date >= info.date_min_anticipee && date < info.date_min);
    const reserver = async () => {
        if (!token || !date || !heure) return;
        if (dateAnticipee && !anticipationCochee) { setErreur("Cochez la demande d'intervention avant la fin du délai de rétractation, ou choisissez une date à partir du " + fmtDateLongue(info?.date_min) + '.'); return; }
        if (lieu === 'domicile' && !adresse.trim()) { setErreur("Merci d'indiquer l'adresse de l'intervention."); return; }
        if (horsContrat && extras.length === 0) { setErreur('Choisissez au moins une prestation.'); return; }
        if (visiteLignes.length > 0 && visiteLignes.every((l) => l.q === 0)) { setErreur('Choisissez ce que nous ferons lors de cette visite.'); return; }
        setEnvoi(true); setErreur('');
        try {
            const r: any = await publicPrestationReservationApi.reserve(token, {
                date, start_time: heure, location_type: lieu,
                address: adresse.trim(), postal_code: codePostal.trim(), city: ville.trim(),
                passage_number: horsContrat ? null : (passageChoisi ?? null),
                extras: extras.length > 0 ? extras : undefined,
                hors_contrat: horsContrat || undefined,
                lignes: !horsContrat && visiteLignes.length > 0 ? visiteLignes.filter((l) => l.q > 0).map((l) => ({ catalog_item_id: l.catalog_item_id ?? null, label: l.label, quantity: l.q })) : undefined,
                execution_anticipee: dateAnticipee ? anticipationCochee : undefined,
            });
            if (r?.error) { setErreur(r.error); return; }
            // Paiement obligatoire avant confirmation (réglage de l'établissement) : la réservation existe
            // déjà côté serveur (en attente), mais ne devient définitive qu'au retour de paiement — on
            // envoie donc directement vers la page de paiement Stripe/Revolut, jamais "confirme" ici.
            if (r?.pending_payment && r?.payment_link) {
                window.location.href = r.payment_link;
                return;
            }
            setResteApres(visiteLignes.length > 0 ? visiteLignes.reduce((n, l) => n + (l.quantity - l.q), 0) : 0);
            setConfirme({ date, start_time: heure });
        } catch (e: any) {
            setErreur(e?.message || "Votre réservation n'a pas pu être enregistrée.");
        } finally { setEnvoi(false); }
    };

    if (paymentReturn === 'ok') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4 text-center">
                <CheckCircle2 size={36} className="mx-auto mb-3 text-emerald-500" />
                <p className="text-content font-bold mb-1">Paiement confirmé</p>
                <p className="text-content-muted text-sm">Votre réservation est confirmée. Un email récapitulatif vous a été envoyé.</p>
            </div>
        );
    }
    if (paymentReturn === 'error') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4 text-center">
                <p className="text-content font-bold mb-1">Paiement non confirmé</p>
                <p className="text-content-muted text-sm">Votre réservation n'a pas pu être confirmée — le paiement n'a pas abouti. Merci de recommencer, ou de contacter directement l'établissement.</p>
            </div>
        );
    }

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }
    if (!info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{erreur || "Ce lien n'est plus valide."}</p>
            </div>
        );
    }

    const logo = resolveLogoUrl(info.logo_url);
    const champ = 'w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
    const rdvConfirme = confirme;
    // Plusieurs passages restants (2026-09-26) : le client choisit lequel planifier — le cas courant (un
    // seul restant, ou devis classique) le présélectionne déjà à l'ouverture.
    const plusieursPassages = info.passages_restants.length > 1 && !horsContrat;
    const catalogueExtrasAffiche = horsContrat ? (info.hors_contrat_catalogue || []) : info.catalogue_extras;
    const extrasDejaChoisis = new Set(extras.map((e) => e.catalog_item_id));

    return (
        <div className="min-h-screen bg-white py-10 px-4">
            <div className="max-w-lg mx-auto space-y-5">
                <div className="text-center space-y-2">
                    {logo && <img src={logo} alt={info.company_name} className="h-14 mx-auto object-contain" />}
                    <h1 className="text-xl font-black text-content">{info.company_name}</h1>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Votre rendez-vous</p>
                        <p className="text-lg font-bold text-content mt-0.5">{info.label}</p>
                    </div>

                    {info.rendez_vous_a_venir.length > 0 && (
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Déjà planifiés</p>
                            {info.rendez_vous_a_venir.map((r, i) => (
                                <p key={i} className="text-xs font-semibold text-content flex items-center gap-1.5">
                                    <CalendarClock size={12} className="text-content-muted" /> {fmtDateLongue(r.date)} à {r.start_time}
                                    {r.passage != null ? ` — passage ${r.passage}` : ''}
                                </p>
                            ))}
                        </div>
                    )}

                    {!rdvConfirme && plusieursPassages && (
                        <div className="space-y-2">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Quel passage souhaitez-vous réserver ?</p>
                            <div className="space-y-1.5">
                                {info.passages_restants.map((p) => (
                                    <label key={p.numero} className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-colors ${passageChoisi === p.numero ? 'border-brand-500 bg-brand-500/5' : 'border-border hover:border-brand-500/40'}`}>
                                        <input type="radio" className="mt-1 accent-brand-600" checked={passageChoisi === p.numero}
                                            onChange={() => { setPassageChoisi(p.numero); setDate(''); setHeure(''); }} />
                                        <span className="min-w-0 flex-1">
                                            <span className="text-sm font-bold text-content">Passage {p.numero}{p.periode ? ` — ${p.periode}` : ''}</span>
                                            <span className="flex flex-wrap gap-1.5 mt-1">
                                                {p.lignes.map((l, i) => (
                                                    <span key={i} className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-surface border border-border text-content-muted">
                                                        {l.label}{l.quantity > 1 ? ` × ${l.quantity}` : ''}
                                                    </span>
                                                ))}
                                            </span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {!rdvConfirme && !horsContrat && (info.hors_contrat_catalogue || []).length > 0 && (
                        <button type="button" onClick={() => { setHorsContrat(true); setPassageChoisi(null); setExtras([]); setDate(''); setHeure(''); }}
                            className="w-full text-left text-xs font-bold text-brand-600 hover:underline">
                            Besoin d'un passage en plus de ceux de votre contrat ? Planifier un passage supplémentaire
                        </button>
                    )}
                    {!rdvConfirme && horsContrat && (
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Passage supplémentaire</p>
                            <p className="text-xs text-content-muted">Ce passage s'ajoute à votre contrat. Il est facturé au tarif en vigueur (réductions de volume comprises). Choisissez ce qu'il faut nettoyer, puis une date.</p>
                            <button type="button" onClick={() => { setHorsContrat(false); setExtras([]); setDate(''); setHeure(''); const r = info.passages_restants; setPassageChoisi(r.length === 1 ? r[0].numero : undefined); }} className="text-[11px] font-bold text-content-muted hover:underline">Revenir à mes passages prévus</button>
                        </div>
                    )}

                    {!rdvConfirme && !plusieursPassages && passageActuel && passageActuel.lignes.length > 0 && (
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">
                                {passageActuel.numero != null ? `Passage ${passageActuel.numero}${passageActuel.periode ? ` — ${passageActuel.periode}` : ''}` : 'Prestations convenues'}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {passageActuel.lignes.map((l, i) => (
                                    <span key={i} className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-surface border border-border text-content-muted">
                                        {l.label}{l.quantity > 1 ? ` × ${l.quantity}` : ''}
                                    </span>
                                ))}
                            </div>
                            <p className="text-[11px] text-content-muted flex items-center gap-1.5">
                                <Clock size={12} /> Durée {passageActuel.duree_estimee ? 'estimée ' : ''}: <span className="font-bold text-content">{fmtDuree(passageActuel.duration_minutes)}</span>
                            </p>
                        </div>
                    )}

                    {!rdvConfirme && visiteLignes.length > 0 && (
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2.5">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">Cette visite</p>
                            {passageActuel?.avancement && passageActuel.avancement.total_prevu > 0 && (
                                <p className="text-[11px] text-content-muted">
                                    Déjà réalisé : <b className="text-content">{passageActuel.avancement.total_fait} sur {passageActuel.avancement.total_prevu}</b>
                                    {passageActuel.avancement.nb_visites > 0 ? ` · ${passageActuel.avancement.nb_visites} visite${passageActuel.avancement.nb_visites > 1 ? 's' : ''} prévue${passageActuel.avancement.nb_visites > 1 ? 's' : ''} ou faite${passageActuel.avancement.nb_visites > 1 ? 's' : ''}` : ''}
                                </p>
                            )}
                            <p className="text-xs text-content-muted">Faut-il tout faire en une fois ? Sinon, indiquez la part de cette visite : vous planifierez la suite ensuite.</p>
                            {visiteLignes.map((l) => (
                                <div key={l.cle} className="flex items-center gap-2">
                                    <span className="min-w-0 flex-1 text-xs text-content">{l.label} <span className="text-content-muted">(reste {l.quantity})</span></span>
                                    <input type="number" min={0} max={l.quantity} value={l.q} aria-label={`Quantité de ${l.label} pour cette visite`}
                                        onChange={(ev) => setPartVisite((prev) => ({ ...prev, [l.cle]: Math.max(0, Math.min(l.quantity, Math.round(Number(ev.target.value) || 0))) }))}
                                        className="w-20 bg-surface border border-border rounded-lg px-2 py-1 text-xs text-content text-right outline-none focus:border-brand-500" />
                                    {l.q !== l.quantity && (
                                        <button type="button" onClick={() => setPartVisite((prev) => ({ ...prev, [l.cle]: l.quantity }))} className="text-[11px] font-bold text-brand-600 hover:underline">Tout</button>
                                    )}
                                </div>
                            ))}
                            <p className="text-[11px] text-content-muted flex items-center gap-1.5">
                                <Clock size={12} /> Durée de cette visite : <span className="font-bold text-content">{fmtDuree(dureeVisite)}</span>
                                {visiteLignes.some((l) => l.q < l.quantity) && <span> · Il restera {visiteLignes.reduce((n, l) => n + (l.quantity - l.q), 0)} à planifier</span>}
                            </p>
                        </div>
                    )}

                    {!rdvConfirme && (info.extras_allowed || horsContrat) && passageChoisi !== undefined && (
                        <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2">
                            <p className="text-[10px] font-black uppercase tracking-widest text-content-muted">{horsContrat ? 'Que faut-il nettoyer ?' : 'Ajouter une prestation (facturée en supplément)'}</p>
                            {extras.map((e) => {
                                const ref = catalogueExtrasAffiche.find((c) => c.id === e.catalog_item_id);
                                return (
                                    <div key={e.catalog_item_id} className="flex items-center gap-2">
                                        <span className="min-w-0 flex-1 text-xs text-content truncate">{ref?.label || e.catalog_item_id}</span>
                                        <input type="number" min={1} max={99} value={e.quantity}
                                            onChange={(ev) => changerQuantiteExtra(e.catalog_item_id, Number(ev.target.value))}
                                            className="w-16 bg-surface border border-border rounded-lg px-2 py-1 text-xs text-content text-right outline-none focus:border-brand-500" />
                                        <button type="button" onClick={() => retirerExtra(e.catalog_item_id)} className="text-content-muted hover:text-red-500"><X size={14} /></button>
                                    </div>
                                );
                            })}
                            <select value={extraAjoutId} onChange={(e) => ajouterExtra(e.target.value)} className={champ}>
                                <option value="">+ Ajouter une prestation…</option>
                                {catalogueExtrasAffiche.filter((c) => !extrasDejaChoisis.has(c.id)).map((c) => (
                                    <option key={c.id} value={c.id}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {rdvConfirme ? (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                            <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-bold text-content">Créneau réservé : {fmtDateLongue(rdvConfirme.date)} à {rdvConfirme.start_time}</p>
                                <p className="text-xs text-content-muted mt-0.5">{info.company_name} vous confirme le rendez-vous et l'équipe qui interviendra. Rien d'autre à faire de votre côté.</p>
                                {(resteApres || 0) > 0 && (
                                    <button type="button" onClick={() => window.location.reload()} className="mt-2 text-xs font-bold text-brand-600 hover:underline">
                                        Il reste {resteApres} à planifier : choisir la visite suivante
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (info.etat === 'clos' && !horsContrat) ? (
                        <p className="text-sm text-content-muted">
                            {info.sans_formule
                                ? "Ce dossier n'attend plus de réservation de votre part. Contactez votre interlocuteur si vous avez une question."
                                : 'Toutes vos prestations de l\'année sont déjà planifiées ou réalisées. Contactez votre interlocuteur pour un rendez-vous supplémentaire.'}
                        </p>
                    ) : (passageChoisi === undefined && !horsContrat) ? (
                        <p className="text-xs text-content-muted italic">Choisissez d'abord un passage ci-dessus.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold text-content-muted">Date</label>
                                    {(() => {
                                        // Contrat signé à domicile : rien avant J+8 ; de J+8 à J+14 sur demande cochée ci-dessous.
                                        const plancher = info.date_min_anticipee || info.date_min;
                                        return <input type="date" min={plancher && plancher > aujourdhui() ? plancher : aujourdhui()} value={date} onChange={(e) => { setDate(e.target.value); setAnticipationCochee(false); }} className={champ} />;
                                    })()}
                                    {info.date_min && info.date_min > aujourdhui() && (
                                        <p className="text-[11px] text-content-muted mt-1">
                                            Contrat signé à domicile : intervention possible à partir du {fmtDateLongue(info.date_min)}
                                            {info.date_min_anticipee ? `, ou dès le ${fmtDateLongue(info.date_min_anticipee)} si vous le demandez.` : '.'}
                                        </p>
                                    )}
                                </div>
                                {info.location_mode === 'les_deux' && (
                                    <div>
                                        <label className="text-xs font-bold text-content-muted">Lieu</label>
                                        <select value={lieu} onChange={(e) => setLieu(e.target.value as 'domicile' | 'entreprise')} className={champ}>
                                            <option value="domicile">Chez moi</option>
                                            <option value="entreprise">Dans les locaux de {info.company_name}</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            {date && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold text-content flex items-center gap-1.5"><CalendarClock size={13} /> Créneaux disponibles le {fmtDateLongue(date)}</p>
                                    {slotsLoading ? (
                                        <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-content-muted" /></div>
                                    ) : slots.length === 0 ? (
                                        <p className="text-xs text-content-muted italic">Aucun créneau disponible ce jour-là. Essayez une autre date.</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {slots.map((s) => (
                                                <button key={s} type="button" onClick={() => setHeure(s)}
                                                    className={`px-3 py-1.5 rounded-lg text-sm font-bold border transition-colors ${heure === s ? 'bg-brand-600 text-brand-fg border-brand-600' : 'bg-surface border-border text-content hover:border-brand-500'}`}>
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {lieu === 'domicile' && info.adresse_fixe && (
                                <div className="space-y-1">
                                    <p className="text-xs font-bold text-content flex items-center gap-1.5"><MapPin size={13} /> Adresse de l'intervention</p>
                                    <p className="text-sm text-content rounded-lg bg-surface-soft border border-border px-3 py-2">
                                        {[info.adresse.address, [info.adresse.postal_code, info.adresse.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                                    </p>
                                    <p className="text-[11px] text-content-muted">Adresse enregistrée : l'intervention a toujours lieu ici. Un changement ? Contactez {info.company_name}.</p>
                                </div>
                            )}
                            {lieu === 'domicile' && !info.adresse_fixe && (
                                <div className="space-y-2">
                                    <p className="text-xs font-bold text-content flex items-center gap-1.5"><MapPin size={13} /> Adresse de l'intervention</p>
                                    <input value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder="Numéro et rue" className={champ} />
                                    <div className="grid grid-cols-3 gap-2">
                                        <input value={codePostal} onChange={(e) => setCodePostal(e.target.value)} placeholder="Code postal" className={champ} />
                                        <input value={ville} onChange={(e) => setVille(e.target.value)} placeholder="Ville" className={`${champ} col-span-2`} />
                                    </div>
                                </div>
                            )}

                            {dateAnticipee && (
                                <label className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 cursor-pointer">
                                    <input type="checkbox" checked={anticipationCochee} onChange={(e) => setAnticipationCochee(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600" />
                                    <span className="space-y-1">
                                        <span className="block text-sm font-bold text-content">Intervention avant la fin de mon délai de rétractation</span>
                                        <span className="block text-xs text-content-muted">{info.texte_anticipation}</span>
                                        {info.fin_retractation && <span className="block text-xs text-content-muted">Sans cette demande, la première date possible est le {fmtDateLongue(info.date_min)}. Votre délai de rétractation court jusqu'au {fmtDateLongue(info.fin_retractation)}.</span>}
                                    </span>
                                </label>
                            )}

                            <button type="button" onClick={reserver} disabled={!date || !heure || envoi || (dateAnticipee && !anticipationCochee)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-black disabled:opacity-50">
                                {envoi ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Réserver ce créneau
                            </button>
                        </>
                    )}

                    {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
                </div>

                <p className="text-[10px] text-content-muted text-center">Ce lien vous est personnel. Ne le transmettez pas.</p>
            </div>
        </div>
    );
};
